import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, Flame } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";

const offerings = [
  {
    tag: "For high-ticket businesses",
    title: "Digital Sales Engine",
    icon: Zap,
    price: "Custom engagement",
    desc: "A 6-stage end-to-end client acquisition system. Paid Traffic → Landing Page → Automated Booking → WhatsApp & Email Nurture → Value-First Call → Client Conversion. Installed and optimised weekly.",
    bullets: [
      "6-stage predictable pipeline",
      "AI-powered lead nurture (Email + WhatsApp)",
      "Weekly performance review · 5 core metrics",
      "Sales framework + call scripting",
    ],
    cta: "Explore Digital Sales Engine",
    link: "/services/digital-sales-engine",
    ctaBook: "Book a Strategy Call",
    bookLink: "/book",
  },
  {
    tag: "For founders & experts",
    title: "Personal Branding",
    icon: Flame,
    price: "₹75,000 investment",
    desc: "The 30·30·40 Framework™ — Authority · Relatability · Virality. Build a founder brand on YouTube and Instagram that becomes your trust accelerator, drops ad costs and closes deals faster.",
    bullets: [
      "30·30·40 signature content framework",
      "Niche + competitor intelligence",
      "Monthly content planner · hooks · scripts",
      "Direct 1:1 with Saurabh Khaspuri",
    ],
    cta: "Explore Personal Branding",
    link: "/services/personal-branding",
    ctaBook: "Apply Now",
    bookLink: "/services/personal-branding#intake",
  },
];

export default function ServicesPage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-20 md:pt-20 md:pb-28">
          <Reveal>
            <div className="eyebrow mb-6">Services</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
              Two engines. One <em className="gold-shine font-medium not-italic">unfair advantage</em>.
              <br />
              <span className="text-red-500">Predictable revenue.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-zinc-400 text-base md:text-lg leading-relaxed font-body">
              A Personal Brand builds the trust. A Digital Sales Engine converts it into pipeline. Together
              they compound — dropping your ad costs, raising your close rates and turning strangers into
              paying clients on repeat.
            </p>
            <p className="mt-4 max-w-3xl text-zinc-500 text-sm md:text-base font-body" data-testid="services-seo-line">
              End-to-end <strong className="text-white font-medium">digital marketing services in India</strong> — Meta &amp; Google ads,
              landing pages, lead generation, WhatsApp &amp; email automation and founder-led personal branding, delivered from Delhi NCR.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TWO CARDS */}
      <section className="relative pb-24 md:pb-32 border-t border-white/5 tl-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14">
          <StaggerGroup className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {offerings.map((o, i) => {
              const Icon = o.icon;
              return (
                <StaggerItem key={i}>
                  <div className="luxury-card p-8 md:p-10 h-full flex flex-col" data-testid={`services-offering-${i}`}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-11 h-11 bg-red-500/10 border border-red-500/20 flex items-center justify-center" style={{ borderRadius: 0 }}>
                        <Icon size={18} className="text-red-500" />
                      </div>
                      <div className="eyebrow text-red-400">{o.tag}</div>
                    </div>
                    <h3 className="font-serif-display text-3xl md:text-4xl mb-2 leading-tight">{o.title}</h3>
                    <div className="text-zinc-500 text-sm mb-5 tracking-wide">{o.price}</div>
                    <p className="text-zinc-400 font-body leading-relaxed mb-6">{o.desc}</p>
                    <ul className="space-y-2 mb-8">
                      {o.bullets.map((b, j) => (
                        <li key={j} className="text-sm text-zinc-300 flex items-start gap-2">
                          <span className="text-red-500 mt-1">▸</span>{b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto flex flex-wrap gap-3">
                      <Link to={o.link} className="btn-primary text-sm" data-testid={`services-explore-${i}`}>
                        {o.cta} <ArrowUpRight size={15} />
                      </Link>
                      <Link to={o.bookLink} className="btn-ghost text-sm" data-testid={`services-book-${i}`}>
                        {o.ctaBook}
                      </Link>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* WHY BOTH TOGETHER */}
      <section className="relative py-24 md:py-32 border-t border-white/5 tl-red">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <div className="eyebrow mb-5">Why combine them</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.1]">
              Your Personal Brand becomes the <em className="gold-shine font-medium not-italic">ultimate trust accelerator</em> for your ads.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-zinc-400 font-body leading-relaxed">
              When leads see your ad, research your founder profile, and find high-value content — your ad
              conversion rates jump and acquisition costs drop. That's the compounding effect we design for.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link to="/book" className="btn-primary mt-10" data-testid="services-final-cta">
              Book a Growth Audit <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
